import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "../lib/auth";
import { Loader2, ArrowRight } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/account";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.must_change_password) {
        navigate("/change-password", { state: { from: redirectTo } });
      } else {
        const hasExplicitDestination = !!location.state?.from;
        const dest = hasExplicitDestination
          ? redirectTo
          : (user.role && user.role !== "customer" ? "/admin" : redirectTo);
        navigate(dest);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="max-w-md mx-auto px-6 py-16">
      <p className="text-xs uppercase tracking-widest text-welljio-subtle mb-3">Welcome back</p>
      <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-8">Sign in to your account.</h1>
      <form onSubmit={submit} className="bg-white border border-slate-100 rounded-3xl p-8 space-y-4" data-testid="login-form">
        <input data-testid="login-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full border border-slate-200 rounded-2xl px-5 py-3.5 outline-none focus:border-welljio-accent text-sm" />
        <input data-testid="login-password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full border border-slate-200 rounded-2xl px-5 py-3.5 outline-none focus:border-welljio-accent text-sm" />
        {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
        <button data-testid="login-submit" disabled={loading} type="submit" className="w-full bg-welljio-ink text-white rounded-full py-4 font-medium hover:bg-slate-800 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin"/> Signing in...</> : <>Sign in <ArrowRight className="w-4 h-4" strokeWidth={1.5}/></>}
        </button>
      </form>
      <p className="text-sm text-welljio-muted mt-6 text-center">
        New to Welljio? <Link to="/register" state={location.state} className="text-welljio-accent hover:underline">Create an account</Link>
      </p>
    </div>
  );
}
